import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Form, Formik } from "formik";
import { t } from "ttag";
import * as Yup from "yup";
import { tz } from "moment-timezone";
import { BaseDialog, Button, ChevronLeft, IconButton, Select, TextField, Typography } from "@coolremote/shared";
import { countries } from "../../../screens/SiteManagement/countryList";
import { stateList } from "../../AddEditSite/stateList";
import { isQaBypassCredentials } from "../../../models/devicePublicRegistrationTestBypass";
import { hasCustomerName, hasPersonalDetails, NewUserFlowState, SiteDetailsValues } from "./newUserFlowState";
import { submitRegistration } from "./deviceRegistrationPublicApi";
import { getQaBypassSiteDetails } from "./qaBypassMockData";
import parentStyles from "../DeviceRegistrationPublic.module.css";
import styles from "./NewUserFlow.module.css";

/** User-facing text for the submit's known error codes — see the CM-635 battle plan for the full table. */
const REGISTRATION_ERROR_MESSAGES: Record<string, string> = {
  USERNAME_ALREADY_EXISTS: t`This email is already registered`,
  CUSTOMER_NAME_ALREADY_EXIST: t`This customer name is already taken`,
  DEVICE_BLOCKED: t`This device can't be registered. Please contact CoolAutomation support`,
  DEVICE_NOT_FOUND: t`The serial you entered was not found. Please verify you entered it correctly`,
  DEVICE_IS_ALREADY_REGISTERED: t`The device is already registered to another account, and can not be registered again`,
  DEVICE_IS_NOT_CONNECTED_IT_MUST_BE_CONNECTED_TO_PERFORM_THIS: t`The device is not connected. It must be connected to be registered. Please connect it and try again`,
  NOT_A_VALID_SERIAL_PIN_COMBINATION: t`The serial and pin do not match. Please verify you entered it correctly`,
  // The transaction succeeded but the device-manager association failed afterwards — the
  // account was created, so retrying the same submit will collide on username/customer name.
  INTERNAL_DEVICE_MANAGER_ERROR_DEVICE_NOT_REGISTERED: t`Your account was created, but we couldn't finish connecting your device. Please contact CoolAutomation support to complete the setup`
};

const getRegistrationErrorMessage = (error: unknown): string => {
  const errorCode = error && typeof error === "object" ? (error as { errorCode?: string }).errorCode : undefined;
  if (errorCode && REGISTRATION_ERROR_MESSAGES[errorCode]) {
    return REGISTRATION_ERROR_MESSAGES[errorCode];
  }
  return error instanceof Error ? error.message : t`Something went wrong. Please try again`;
};

const countryOptions = countries.map((country) => ({ value: country.code, label: country.name }));
const timezoneOptions = tz.names().map((timezone) => ({
  value: timezone,
  label: `${timezone}  ${tz(timezone).format("Z")}`
}));

const emptyValues: SiteDetailsValues = {
  name: "",
  address: "",
  country: "",
  city: "",
  postalCode: "",
  description: "",
  timezone: "",
  state: ""
};

const validationSchema = Yup.object({
  name: Yup.string().required(t`Required`),
  address: Yup.string().required(t`Required`),
  country: Yup.string().required(t`Required`),
  city: Yup.string().required(t`Required`),
  postalCode: Yup.string(),
  description: Yup.string(),
  timezone: Yup.string().required(t`Required`),
  state: Yup.string().when("country", {
    is: (value: string) => value === "US",
    then: Yup.string().required(t`Required`),
    otherwise: Yup.string()
  })
});

interface SiteDetailsFormProps {
  initialValues?: Partial<SiteDetailsValues>;
  submitLabel?: string;
  disabled?: boolean;
  onSubmit: (siteDetails: SiteDetailsValues) => void;
}

/**
 * Presentational site-details form, mirroring the fields and validation of
 * the authenticated Add Site screen (`AddEditSite.tsx`). Reused as-is by
 * CM-619 for its "+ Create new site" option — kept free of routing/history.
 */
export const SiteDetailsForm: React.FC<SiteDetailsFormProps> = ({
  initialValues,
  submitLabel = t`Save`,
  disabled = false,
  onSubmit
}) => (
  <Formik
    initialValues={{ ...emptyValues, ...initialValues }}
    validationSchema={validationSchema}
    onSubmit={onSubmit}
  >
    {({ values, errors, touched, setFieldValue }) => (
      <Form className={styles.form}>
        <TextField
          label={`${t`Site Name`} *`}
          fullWidth
          autoComplete="off"
          value={values.name}
          onChange={(v: string) => setFieldValue("name", v)}
          error={touched.name ? errors.name : undefined}
        />
        <TextField
          label={`${t`Street Address`} *`}
          fullWidth
          autoComplete="off"
          value={values.address}
          onChange={(v: string) => setFieldValue("address", v)}
          error={touched.address ? errors.address : undefined}
        />
        <Select
          label={`${t`Country`} *`}
          fullWidth
          searchable
          menuPortal
          value={values.country}
          onChange={(v: string) => setFieldValue("country", v)}
          options={countryOptions}
          error={!!(errors.country && touched.country)}
        />
        {touched.country && errors.country && (
          <Typography variant="caption" color="error">
            {errors.country}
          </Typography>
        )}
        <TextField
          label={`${t`City`} *`}
          fullWidth
          autoComplete="off"
          value={values.city}
          onChange={(v: string) => setFieldValue("city", v)}
          error={touched.city ? errors.city : undefined}
        />
        {values.country === "US" && (
          <>
            <Select
              label={`${t`State`} *`}
              fullWidth
              menuPortal
              value={values.state}
              onChange={(v: string) => setFieldValue("state", v)}
              options={stateList}
              error={!!(errors.state && touched.state)}
            />
            {touched.state && errors.state && (
              <Typography variant="caption" color="error">
                {errors.state}
              </Typography>
            )}
          </>
        )}
        <TextField
          label={t`Zip`}
          fullWidth
          autoComplete="off"
          value={values.postalCode}
          onChange={(v: string) => setFieldValue("postalCode", v)}
        />
        <TextField
          label={t`Description`}
          fullWidth
          value={values.description}
          onChange={(v: string) => setFieldValue("description", v)}
        />
        <Select
          label={`${t`Timezone`} *`}
          fullWidth
          searchable
          menuPortal
          value={values.timezone}
          onChange={(v: string) => setFieldValue("timezone", v)}
          options={timezoneOptions}
          error={!!(errors.timezone && touched.timezone)}
        />
        {touched.timezone && errors.timezone && (
          <Typography variant="caption" color="error">
            {errors.timezone}
          </Typography>
        )}
        <Button type="submit" variant="contained" className={styles.primaryButton} disabled={disabled}>
          {submitLabel}
        </Button>
      </Form>
    )}
  </Formik>
);

/** Step 3 of the new-user branch (CM-618) — route wrapper around `SiteDetailsForm`. */
const SiteDetailsStep: React.FC = () => {
  const history = useHistory<NewUserFlowState | undefined>();
  const deviceState = history.location.state;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!hasCustomerName(deviceState)) {
      history.replace("/register-device/new-user/customer-name");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!hasCustomerName(deviceState) || !hasPersonalDetails(deviceState)) {
    return null;
  }

  const handleBack = () => {
    history.goBack();
  };

  const handleDismissError = () => {
    setErrorMessage(null);
  };

  const handleSubmit = async (siteDetails: SiteDetailsValues) => {
    setIsSubmitting(true);
    // Attach the typed values to *this* history entry too, so that going back
    // from Discovery (or retrying after a failed submit) lands here with the
    // form restored instead of blank.
    history.replace(history.location.pathname, { ...deviceState, siteDetails } as NewUserFlowState);
    try {
      const { customerId, siteId, deviceId } = await submitRegistration(
        deviceState.serial,
        deviceState.pin,
        deviceState.personalDetails,
        deviceState.customerName,
        siteDetails
      );
      history.push("/register-device/new-user/discovery", {
        ...deviceState,
        siteDetails,
        customerId,
        siteId,
        deviceId
      } as NewUserFlowState);
      // No setIsSubmitting(false) here — the push above unmounts this component.
    } catch (error: unknown) {
      setErrorMessage(getRegistrationErrorMessage(error));
      setIsSubmitting(false);
    }
  };

  return (
    <div className={parentStyles.screen}>
      <div className={parentStyles.appBar}>
        <IconButton className={parentStyles.backButton} onClick={handleBack} aria-label={t`Back`}>
          <ChevronLeft width={20} height={20} />
        </IconButton>
        <span className={parentStyles.appBarTitle}>{t`Add Site`}</span>
        <span />
      </div>

      <div className={styles.stepContent}>
        <p className={styles.stepSubtitle}>{t`Please add site information`}</p>
        <SiteDetailsForm
          initialValues={{
            ...(isQaBypassCredentials(deviceState.serial, deviceState.pin)
              ? getQaBypassSiteDetails()
              : {}),
            ...deviceState.siteDetails,
            name: deviceState.siteDetails?.name ?? deviceState.customerName
          }}
          disabled={isSubmitting}
          onSubmit={handleSubmit}
        />
      </div>

      <BaseDialog
        open={!!errorMessage}
        onClose={handleDismissError}
        onConfirm={handleDismissError}
        title={t`Registration error`}
        confirmLabel={t`OK`}
        hideCancel
      >
        <Typography>{errorMessage}</Typography>
      </BaseDialog>
    </div>
  );
};

export default SiteDetailsStep;
